import React, { forwardRef } from 'react';
import { Button } from 'primereact/button';
import type { ButtonProps } from 'primereact/button';
import './ButtonAtom.scss';

export type ButtonShape =
  | 'pill'
  | 'square-left'
  | 'square-right'
  | 'rectangle'
  | 'icon-square';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'text'
  | 'pagination'
  | 'ghost'
  | 'danger'
  | 'dashed';

export type ButtonSize = 'small' | 'normal' | 'large';

/** A state overlay on top of `variant` — `warn` renders the same warning-surface treatment the design system already gives other atoms (see `ButtonAtom.scss`'s `&[data-tone='warn']`). */
export type ButtonTone = 'warn';

export interface ButtonAtomProps
  extends Omit<ButtonProps, 'className' | 'size'> {
  shape?: ButtonShape;
  variant?: ButtonVariant;
  size?: ButtonSize;
  tone?: ButtonTone;
  /** PrimeReact Button’s iconPos prop */
  iconPos?: 'left' | 'right';
  className?: string;
}

const ButtonAtom = forwardRef<Button, ButtonAtomProps>((props, ref) => {
  const {
    shape = 'pill',
    variant = 'primary',
    size = 'normal',
    tone,
    iconPos = 'left',
    className,
    // Default to `type="button"` so a ButtonAtom rendered inside a Formik/native form doesn't
    // accidentally submit it. HTML's own default is `type="submit"` — Formik surfaces this as a
    // dev warning; PrimeReact does not override it. Consumers that actually intend a submit button
    // pass `type="submit"` explicitly (that's the React ergonomic anyway). Caught by EAINT-877's
    // "Always include a sphere" button — clicking it was submitting SphereEditor's form and
    // closing the whole Edit Sphere dialog.
    type = 'button',
    ...rest
  } = props;

  // build our CSS classes
  const classes = [
    'button-atom',
    `button-atom--shape-${shape}`,
    `button-atom--var-${variant}`,
    `button-atom--size-${size}`,
    className || ''
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className="prime-button-wrapper">
      <Button
        ref={ref}
        className={classes}
        iconPos={iconPos}
        data-tone={tone}
        type={type}
        {...rest}
      />
    </div>
  );
});

ButtonAtom.displayName = 'ButtonAtom';

export default ButtonAtom;
